import React, { createContext, useContext, useMemo, useCallback } from "react";

/**
 * Service shape (reference)
 * {
 *   id: "svc_microsoft",
 *   slug: "microsoft",
 *   title: "Microsoft Solutions & Cloud",
 *   short: "M365 • Azure • Security • Migration",
 *   category: "IT",
 *   tags: ["m365","azure","security","copilot"],
 *   hero: { headline, sub, image, cta: { label, to } },
 *   features: [{ title, desc }],
 *   plans: [{ name, price, unit, items: [] }],
 *   faqs: [{ q, a }],
 *   regions: { USA:{topCompanies:[]}, UK:{}, AUS:{} },
 *   seo: { title, description, schemaType: "Service" }
 * }
 */

const ServiceContext = createContext(null);

const initialServices = [
  {
    id: "svc_microsoft",
    slug: "microsoft",
    title: "Microsoft Solutions & Cloud",
    short: "M365 • Azure • Security • Migration",
    category: "IT",
    tags: ["m365", "azure", "security", "copilot"],
    hero: {
      headline: "Modernize on Microsoft—securely, quickly, cost-effectively.",
      sub: "M365 setup, Azure migration, identity hardening, Copilot enablement.",
      image: "/img/hero/microsoft.jpg",
      cta: { label: "Get a Microsoft Assessment", to: "/contact?topic=microsoft" },
    },
    features: [
      { title: "M365 Setup & Hardening", desc: "Baseline policies, DLP, Defender." },
      { title: "Azure Landing Zone", desc: "IaaS/PaaS, cost governance, tagging." },
      { title: "Identity & Access", desc: "Entra ID, CA, MFA, role hygiene." },
      { title: "Copilot Readiness", desc: "Data mapping, permissions, pilots." },
    ],
    plans: [
      { name: "Starter", price: 499, unit: "one-time", items: ["Tenant baseline", "Email hardening"] },
      { name: "Managed", price: 599, unit: "/mo", items: ["Monitoring", "Incidents", "Reports"] },
    ],
    faqs: [
      { q: "Do you help with Copilot?", a: "Yes—data readiness, pilots, adoption & governance." },
    ],
    regions: { USA: { topCompanies: [] }, UK: { topCompanies: [] }, AUS: { topCompanies: [] } },
    seo: { title: "Microsoft Services", description: "M365, Azure, security & Copilot enablement.", schemaType: "Service" },
  },
  {
    id: "svc_accounting",
    slug: "accounting-bookkeeping",
    title: "Accounting & Bookkeeping",
    short: "Monthly books • Payroll • AR/AP • Cleanup",
    category: "Finance",
    tags: ["bookkeeping", "payroll", "cfo", "reports"],
    hero: {
      headline: "Accurate books. On time. Audit-ready.",
      sub: "Bookkeeping, payroll, reconciliation, monthly closes & CFO insights.",
      image: "/img/hero/accounting.jpg",
      cta: { label: "Free Health Check", to: "/contact?topic=accounting" },
    },
    features: [
      { title: "Monthly Bookkeeping", desc: "Bank feeds, reconciliations, P&L, BS." },
      { title: "Payroll & Compliance", desc: "Setup, filings, payslips." },
      { title: "Catch-up & Cleanup", desc: "Fix historical gaps & errors." },
      { title: "CFO Reports", desc: "KPIs, cash flow, budget vs actual." },
    ],
    plans: [
      { name: "Starter", price: 199, unit: "/mo", items: ["Up to 100 txns", "Monthly reports"] },
      { name: "Growth", price: 399, unit: "/mo", items: ["Up to 500 txns", "Payroll add-on", "Quarterly CFO deck"] },
    ],
    faqs: [{ q: "Which software?", a: "QuickBooks, Xero, Zoho Books, Sage (select), Tally mapping." }],
    regions: { USA: {}, UK: {}, AUS: {} },
    seo: { title: "Accounting & Bookkeeping", description: "Books, payroll, CFO reports.", schemaType: "Service" },
  },
  {
    id: "svc_printer",
    slug: "printer-support",
    title: "Printer Sales & Support",
    short: "Install • Drivers • Networking • Consumables",
    category: "Hardware",
    tags: ["printer", "cartridge", "fleet", "support"],
    hero: {
      headline: "Printers that just work—plus consumables on time.",
      sub: "Installations, drivers, Wi-Fi/LAN, managed consumables, rapid troubleshooting.",
      image: "/img/hero/printer.jpg",
      cta: { label: "Fix My Printer", to: "/contact?topic=printer" },
    },
    features: [
      { title: "Install & Configure", desc: "USB/Wi-Fi/Ethernet, drivers, sharing." },
      { title: "Managed Consumables", desc: "Cartridges, drums, maintenance kits." },
      { title: "SMB Fleet", desc: "Policy, costs, uptime SLAs." },
      { title: "Scan & Fax", desc: "To email/cloud/SMB, workflows." },
    ],
    plans: [],
    faqs: [],
    regions: {
      USA: { topCompanies: ["HP", "Brother", "Canon", "Epson", "Xerox", "Lexmark"] },
      UK: { topCompanies: ["HP", "Brother", "Canon", "Epson", "Kyocera"] },
      AUS: { topCompanies: ["HP", "Brother", "Epson", "Canon", "FUJIFILM"] },
    },
    seo: { title: "Printer Support", description: "Sales, setup & consumables.", schemaType: "Service" },
  },
  {
    id: "svc_antivirus",
    slug: "antivirus",
    title: "Antivirus & Endpoint Security",
    short: "Licenses • EDR • Deployment • Monitoring",
    category: "Security",
    tags: ["edr", "av", "defender", "endpoint"],
    hero: {
      headline: "Protect every device with enterprise-grade security.",
      sub: "AV/EDR deployment, policy hardening, monitoring & response.",
      image: "/img/hero/antivirus.jpg",
      cta: { label: "Secure My Devices", to: "/contact?topic=antivirus" },
    },
    features: [
      { title: "AV/EDR Rollout", desc: "Windows/Mac policies, exclusions." },
      { title: "License Management", desc: "Renewals, seats, audits." },
      { title: "SOC-Light", desc: "Alerts triage, playbooks." },
      { title: "Reporting", desc: "Coverage, posture, compliance." },
    ],
    plans: [],
    faqs: [],
    regions: {
      USA: { topCompanies: ["Norton", "McAfee", "Bitdefender", "ESET", "Kaspersky", "Trend Micro"] },
      UK: { topCompanies: ["Bitdefender", "ESET", "Sophos", "Norton", "McAfee"] },
      AUS: { topCompanies: ["Trend Micro", "Norton", "Bitdefender", "ESET"] },
    },
    seo: { title: "Antivirus & EDR", description: "Endpoint security services & licensing.", schemaType: "Service" },
  },
  {
    id: "svc_web",
    slug: "web-development",
    title: "Web Development",
    short: "React • WordPress • Headless • SEO",
    category: "Engineering",
    tags: ["react", "wordpress", "seo", "headless"],
    hero: {
      headline: "Fast, accessible, SEO-ready websites that convert.",
      sub: "Design systems, component libraries, headless CMS, CI/CD.",
      image: "/img/hero/webdev.jpg",
      cta: { label: "Start a Web Project", to: "/contact?topic=web" },
    },
    features: [
      { title: "React/Next.js", desc: "SSG/ISR, API routes, auth." },
      { title: "WordPress Pro", desc: "ACF, CPTs, performance." },
      { title: "Headless CMS", desc: "Contentful/Strapi/Sanity." },
      { title: "SEO & CWV", desc: "Schema, sitemaps, speed." },
    ],
    plans: [
      { name: "Landing", price: 499, unit: "one-time", items: ["1 page", "Form", "Analytics"] },
      { name: "Business", price: 1499, unit: "one-time", items: ["5–7 pages", "Blog", "On-page SEO"] },
    ],
    faqs: [{ q: "Maintenance?", a: "Yes—patching, backups, monitoring, minor changes." }],
    regions: {},
    seo: { title: "Web Development Services", description: "React, WordPress, headless builds.", schemaType: "Service" },
  },
  {
    id: "svc_crm",
    slug: "crm-development",
    title: "CRM Development",
    short: "Workflows • Integrations • Dashboards",
    category: "Engineering",
    tags: ["hubspot", "salesforce", "automation"],
    hero: {
      headline: "CRMs that your team actually loves using.",
      sub: "Custom entities, automations, pipelines & integrations.",
      image: "/img/hero/crm.jpg",
      cta: { label: "Design My CRM", to: "/contact?topic=crm" },
    },
    features: [
      { title: "Pipelines & Stages", desc: "Sales, service, onboarding." },
      { title: "Automations", desc: "Tasks, emails, SLAs, escalations." },
      { title: "Integrations", desc: "Billing, support, marketing ops." },
      { title: "Dashboards", desc: "KPIs, targets, performance." },
    ],
    plans: [{ name: "Implementation", price: 999, unit: "one-time", items: ["Discovery", "Setup", "Training"] }],
    faqs: [{ q: "Migrate from sheets?", a: "Yes—import, normalize & validate data." }],
    regions: {},
    seo: { title: "CRM Development", description: "HubSpot/Salesforce implementations.", schemaType: "Service" },
  },
];

export const ServiceProvider = ({ children, seed = initialServices }) => {
  // --- Core dataset ---
  const services = useMemo(() => seed, [seed]);

  // --- Selectors ---
  const getAll = useCallback(() => services, [services]);
  const getBySlug = useCallback(
    (slug) => services.find((s) => s.slug === slug) || null,
    [services]
  );
  const getById = useCallback(
    (id) => services.find((s) => s.id === id) || null,
    [services]
  );
  const listByCategory = useCallback(
    (category) => services.filter((s) => s.category === category),
    [services]
  );
  const search = useCallback(
    (q) => {
      const t = String(q || "").toLowerCase().trim();
      if (!t) return services;
      return services.filter((s) =>
        [s.title, s.short, s.category, ...(s.tags || [])]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(t))
      );
    },
    [services]
  );

  // --- Minimal mutable stubs (you can swap to reducer or backend later) ---
  const addService = useCallback(
    (svc) => {
      console.warn("addService called — provide state management or backend to persist", svc);
    },
    []
  );
  const updateService = useCallback((id, patch) => {
    console.warn("updateService called — provide state management or backend to persist", id, patch);
  }, []);
  const removeService = useCallback((id) => {
    console.warn("removeService called — provide state management or backend to persist", id);
  }, []);

  const value = useMemo(
    () => ({
      services,
      // selectors
      getAll,
      getBySlug,
      getById,
      listByCategory,
      search,
      // mutations (no-op stubs for now)
      addService,
      updateService,
      removeService,
    }),
    [services, getAll, getBySlug, getById, listByCategory, search, addService, updateService, removeService]
  );

  return <ServiceContext.Provider value={value}>{children}</ServiceContext.Provider>;
};

export const useServices = () => {
  const ctx = useContext(ServiceContext);
  if (!ctx) throw new Error("useServices must be used within <ServiceProvider />");
  return ctx;
};
